/**
 * altColleges.js
 *
 * Schools a player attended BEYOND the one college stored in the roster CSVs.
 * Kept here rather than in the CSVs so the daily-deck rotations (seeded off CSV
 * row order) never reshuffle when this list grows.
 *
 * TWO SEPARATE LISTS, because they are used differently (rule set 2026-08-19):
 *
 * UNDERGRAD_ALTS - other UNDERGRADUATE schools (transfers, juco, D-II starts).
 *   - Hard mode ACCEPTS these as correct typed answers.
 *   - Multiple choice never offers them as wrong options.
 *
 * POSTGRAD_SCHOOLS - law school, MBA, med school, master's, MFA, PhD, Rhodes.
 *   - Hard mode does NOT accept these: the game is about undergrad.
 *   - Multiple choice still never offers them as wrong options, because seeing
 *     "Harvard" on a Michelle Obama question is confusing even though the
 *     answer we want is Princeton.
 *
 * An athlete who transferred as an NCAA "graduate transfer" but was still
 * playing counts as UNDERGRAD here - the distinction is academic postgraduate
 * study, not eligibility status.
 *
 * Keys must match the roster CSV player name EXACTLY. School names must be in
 * the canonical form normalizeCollege() produces (e.g. "Pittsburgh", not "Pitt")
 * or the matcher will not accept them.
 *
 * Built 2026-08-19 from a verified sweep of all 1,919 roster rows: proposed by
 * one agent, fact-checked by a second, then each school classified undergrad vs
 * postgrad. Commitments-only (never enrolled) and unverifiable claims dropped.
 */

const UNDERGRAD_ALTS = {
  // NFL
  "Aaron Rodgers": ["Butte College"],
  "Alvin Kamara": ["Alabama"],
  "Andy Reid": ["Glendale Community College"],
  "Antonio Gates": ["College of the Sequoias", "Eastern Michigan", "Michigan State"],
  "Antonio Gibson": ["East Central CC"],
  "Bailey Zappe": ["Houston Baptist"],
  "Baker Mayfield": ["Texas Tech"],
  "BenJarvus Green-Ellis": ["Indiana"],
  "Bill Parcells": ["Colgate"],
  "Bill Walsh": ["College of San Mateo"],
  "Bo Nix": ["Auburn"],
  "Brandon Aiyuk": ["Sierra College"],
  "Brandon Jacobs": ["Auburn", "Coffeyville Community College"],
  "Caleb Williams": ["Oklahoma"],
  "Cam Newton": ["Blinn College", "Florida"],
  "Cam Ward": ["Incarnate Word", "Washington State"],
  "Chad Johnson": ["Langston", "Santa Monica College"],
  "Chris Carson": ["Butler Community College"],
  "Christian Gonzalez": ["Colorado"],
  "Cordarrelle Patterson": ["Hutchinson Community College"],
  "Corey Dillon": ["Dixie State", "Garden City CC"],
  "Dalton Kincaid": ["San Diego"],
  "Darius Slay": ["Itawamba CC"],
  "Deion Branch": ["Jones County Junior College"],
  "Delanie Walker": ["Mt. San Antonio College"],
  "Fernando Mendoza": ["California"],
  "Gardner Minshew": ["East Carolina"],
  "Gus Edwards": ["Miami (FL)"],
  "Isaac Bruce": ["Santa Monica College", "West Los Angeles College"],
  "J.J. Watt": ["Central Michigan"],
  "Jacoby Brissett": ["Florida"],
  "Jaelan Phillips": ["UCLA"],
  "Jahmyr Gibbs": ["Georgia Tech"],
  "Jalen Hurts": ["Alabama"],
  "Jalyx Hunt": ["Cornell"],
  "Jamal Anderson": ["Moorpark College"],
  "Jameson Williams": ["Ohio State"],
  "Jared Verse": ["Albany"],
  "Jason Pierre-Paul": ["College of the Canyons", "Fort Scott CC"],
  "Jaxson Dart": ["USC"],
  "Jayden Daniels": ["Arizona State"],
  "Jayden Reed": ["Western Michigan"],
  "Jeff Garcia": ["Gavilan College"],
  "Jeremy Shockey": ["Northeastern Oklahoma A&M"],
  "Joe Burrow": ["Ohio State"],
  "Joe Flacco": ["Pittsburgh"],
  "Joe Gibbs": ["Cerritos College"],
  "John Brown": ["Coffeyville Community College", "Mars Hill"],
  "John Madden": ["College of San Mateo", "Grays Harbor College", "Oregon"],
  "Johnathan Joseph": ["Coffeyville Community College"],
  "Jon Gruden": ["Muskingum"],
  "Jordan Addison": ["Pittsburgh"],
  "Josh Allen": ["Reedley College"],
  "Josh Gordon": ["Utah"],
  "Josh McCown": ["SMU"],
  "Julian Edelman": ["College of San Mateo"],
  "Justin Fields": ["Georgia"],
  "Kendrell Bell": ["Middle Georgia Junior College"],
  "Kenneth Walker": ["Wake Forest"],
  "Kenny Golladay": ["North Dakota"],
  "Kevin Curtis": ["Snow College"],
  "Keyshawn Johnson": ["West Los Angeles College"],
  "Kyler Murray": ["Texas A&M"],
  "Landon Dickerson": ["Florida State"],
  "Lane Johnson": ["Kilgore College"],
  "Lavonte David": ["Fort Scott CC"],
  "Luke McCaffrey": ["Louisville", "Nebraska"],
  "Malik Willis": ["Auburn"],
  "Marquise Brown": ["College of the Canyons"],
  "Matt LaFleur": ["Western Michigan"],
  "Mel Gray": ["Coffeyville Community College"],
  "Mike Green": ["Virginia"],
  "Montez Sweat": ["Copiah-Lincoln CC", "Michigan State"],
  "Nathan Peterman": ["Tennessee"],
  "Nick Foles": ["Michigan State"],
  "Pete Carroll": ["College of Marin"],
  "Pierre Garcon": ["Norwich"],
  "Puka Nacua": ["Washington"],
  "Rachaad White": ["Mt. San Antonio College", "Nebraska-Kearney"],
  "Randy Moss": ["Florida State"],
  "Rhamondre Stevenson": ["Cerritos College"],
  "Roger Staubach": ["New Mexico Military Institute"],
  "Russell Wilson": ["NC State"],
  "Ryan Ramczyk": ["Madison Area Technical College", "Mid-State Technical College", "UW-Stevens Point"],
  "Shaquil Barrett": ["Nebraska-Omaha"],
  "Shonn Greene": ["Kirkwood Community College"],
  "Steve Smith Sr.": ["Santa Monica College"],
  "TJ Houshmandzadeh": ["Cerritos College"],
  "Terrell Davis": ["Long Beach State"],
  "Travis Hunter": ["Jackson State"],
  "Troy Aikman": ["Oklahoma"],
  "Tyreek Hill": ["Garden City CC", "Oklahoma State"],
  "Wan'Dale Robinson": ["Nebraska"],
  "Warren Moon": ["West Los Angeles College"],
  "Will Levis": ["Penn State"],
  "Za'Darius Smith": ["East Mississippi CC"],

  // NBA
  "Alex Morales": ["Prince George's Community College"],
  "Alfonzo McKinnie": ["Eastern Illinois"],
  "Artis Gilmore": ["Gardner-Webb"],
  "Austin Reaves": ["Wichita State"],
  "Ben Wallace": ["Cuyahoga Community College"],
  "Bob McAdoo": ["Vincennes"],
  "Brandin Podziemski": ["Illinois"],
  "Brian Shaw": ["Saint Mary's"],
  "Bryn Forbes": ["Cleveland State"],
  "Caleb Martin": ["NC State"],
  "Cam Spencer": ["Loyola Maryland", "Rutgers"],
  "Cameron Johnson": ["Pittsburgh"],
  "Cedric Coward": ["Eastern Washington", "Willamette"],
  "Chris Boucher": ["New Mexico Junior College", "Northwest College"],
  "Dalton Knecht": ["Northeastern Junior College", "Northern Colorado"],
  "Danny Granger": ["Bradley"],
  "Davion Mitchell": ["Auburn"],
  "Delon Wright": ["City College of San Francisco"],
  "Dennis Rodman": ["Cooke County JC"],
  "Derrick White": ["Colorado-Colorado Springs"],
  "Devin Carter": ["South Carolina"],
  "Dorian Finney-Smith": ["Virginia Tech"],
  "Duncan Robinson": ["Williams"],
  "Elgin Baylor": ["College of Idaho"],
  "Enes Kanter": ["Kentucky"],
  "Gary Payton II": ["Salt Lake Community College"],
  "George Gervin": ["Long Beach State"],
  "Ime Udoka": ["College of Eastern Utah", "San Francisco"],
  "Jae Crowder": ["Howard College", "South Georgia Tech"],
  "Jamaal Tinsley": ["Mount San Jacinto College"],
  "Jamal Cain": ["Marquette"],
  "Jaylen Wells": ["Sonoma State"],
  "Jaylon Tyson": ["Texas", "Texas Tech"],
  "Jeff Van Gundy": ["Menlo College", "SUNY Brockport", "Yale"],
  "Jerry Sloan": ["Illinois", "Southern Illinois"],
  "Jimmy Butler": ["Tyler Junior College"],
  "Jordan Clarkson": ["Tulsa"],
  "Kel'el Ware": ["Oregon"],
  "Kendrick Nunn": ["Illinois"],
  "Khem Birch": ["Pittsburgh"],
  "Larry Bird": ["Indiana"],
  "Larry Johnson": ["Odessa College"],
  "Loren Woods": ["Wake Forest"],
  "Max Strus": ["Lewis"],
  "Mitch Richmond": ["Moberly Area Community College"],
  "Mitchell Robinson": ["Western Kentucky"],
  "Nate Archibald": ["Arizona Western"],
  "Nate Darling": ["Alabama Birmingham"],
  "Nick Van Exel": ["Trinity Valley Community College"],
  "Quentin Grimes": ["Kansas"],
  "Richaun Holmes": ["Moraine Valley"],
  "Ricky Council IV": ["Wichita State"],
  "Royce O'Neale": ["Denver"],
  "Sam Cassell": ["San Jacinto College"],
  "Seth Curry": ["Liberty"],
  "Shawn Kemp": ["Kentucky"],
  "Shawn Marion": ["Vincennes"],
  "Sonny Weems": ["Arkansas-Fort Smith"],
  "Spencer Haywood": ["Trinidad State Junior College"],
  "Spud Webb": ["Midland College"],
  "Stephen Graham": ["UCF"],
  "Steve Francis": ["Allegany College of Maryland", "San Jacinto College"],
  "Tari Eason": ["Cincinnati"],
  "Tony Allen": ["Butler County CC", "Wabash Valley"],
  "Trevelin Queen": ["College of Marin", "New Mexico Military Institute"],
  "Trey Murphy III": ["Rice"],
  "Tyler Kolek": ["George Mason"],
  "Walker Kessler": ["North Carolina"],

  // Mish Mosh
  "2 Chainz": ["Virginia State"],
  "Adam Driver": ["University of Indianapolis"],
  "Adam McKay": ["Penn State"],
  "Alec Baldwin": ["NYU"],
  "Anne Hathaway": ["Vassar"],
  "Barack Obama": ["Occidental"],
  "Barry Zito": ["Los Angeles Pierce College", "UC Santa Barbara"],
  "Ben Affleck": ["Vermont"],
  "Bob Beamon": ["Adelphi", "North Carolina A&T"],
  "Bob Odenkirk": ["College of DuPage", "Marquette"],
  "Bradley Cooper": ["Villanova"],
  "Brock Lesnar": ["Bismarck State College"],
  "Chevy Chase": ["Haverford"],
  "Diplo": ["UCF"],
  "Donald Trump": ["Fordham"],
  "Elon Musk": ["Queen's University"],
  "George Lucas": ["Modesto Junior College"],
  "Greg Louganis": ["UC Irvine"],
  "Hunter Pence": ["Texarkana College"],
  "Jack Dorsey": ["Missouri S&T"],
  "Jake Arrieta": ["Weatherford College"],
  "Jerry Seinfeld": ["SUNY Oswego"],
  "Jimmy Kimmel": ["UNLV"],
  "John Grisham": ["Delta State", "Northwest Mississippi Community College"],
  "Jon Hamm": ["Texas"],
  "Kanye West": ["American Academy of Art"],
  "Larry Ellison": ["Chicago"],
  "Lou Reed": ["NYU"],
  "Marco Rubio": ["Santa Fe College", "Tarkio College"],
  "Mark Cuban": ["Pittsburgh"],
  "Mark Prior": ["Vanderbilt"],
  "Megan Thee Stallion": ["Prairie View A&M"],
  "Meryl Streep": ["Dartmouth"],
  "Mitt Romney": ["Stanford"],
  "Nastia Liukin": ["SMU"],
  "Oliver Stone": ["Yale"],
  "Paul Newman": ["Ohio University"],
  "Paul Skenes": ["Air Force"],
  "Robin Williams": ["Claremont McKenna", "College of Marin"],
  "Roger Clemens": ["San Jacinto College"],
  "Ryan Coogler": ["Saint Mary's"],
  "Sandy Koufax": ["Columbia"],
  "Sofia Coppola": ["Mills College"],
  "Stephen A. Smith": ["Fashion Institute of Technology"],
  "Steve Wozniak": ["Colorado", "De Anza College"],
  "Stone Cold Steve Austin": ["Wharton County Junior College"],
  "The Undertaker": ["Angelina College"],
  "Timoth\u00e9e Chalamet": ["NYU"],
  "Tom Hanks": ["Chabot College"],
  "Tom Seaver": ["Fresno City College"],
  "Ty Burrell": ["Southern Oregon"],
  "Warren Buffett": ["Penn"],
  "Xander Schauffele": ["Long Beach State"],

  // --- 2026-10-29 roster batch (+90) ---
  "Shedeur Sanders": ["Jackson State"],
  "Cam Skattebo": ["Sacramento State"],
  "Michael Penix Jr.": ["Tennessee"],
  "Bucky Irving": ["Minnesota"],
  "Spencer Rattler": ["Oklahoma"],
  "Mark Gastineau": ["Eastern Arizona", "Arizona State"],
  "Walter Jones": ["Holmes CC"],
  "Walter Clayton Jr.": ["Iona"],
  "Latrell Sprewell": ["Three Rivers CC"],
  "Mookie Blaylock": ["Midland College"],
  "Steve Martin": ["Santa Ana College", "Long Beach State"],
  "Rainn Wilson": ["Tufts"],
  "Jimmy Buffett": ["Auburn", "Pearl River CC"],
  "Holly Rowe": ["BYU"],
  "Ed O'Neill": ["Ohio"],
};

const POSTGRAD_SCHOOLS = {
  // NFL
  "Howie Roseman": ["Fordham"],
  "Urban Meyer": ["Ohio State"],

  // NBA
  "Bill Walton": ["Stanford"],

  // Mish Mosh
  "Al Gore": ["Vanderbilt"],
  "Barack Obama": ["Harvard"],
  "Bill O'Reilly": ["Boston University", "Harvard"],
  "Bill Simmons": ["Boston University"],
  "Bradley Cooper": ["The New School"],
  "Condoleezza Rice": ["Notre Dame"],
  "David Foster Wallace": ["Arizona"],
  "Demetri Martin": ["NYU"],
  "Francis Ford Coppola": ["UCLA"],
  "Frank Shorter": ["Florida", "New Mexico"],
  "Franklin D. Roosevelt": ["Columbia"],
  "George W. Bush": ["Harvard"],
  "Gillian Flynn": ["Northwestern"],
  "Henry Winkler": ["Yale"],
  "Hillary Clinton": ["Yale"],
  "JFK Jr.": ["NYU"],
  "James Franco": ["Columbia", "NYU", "Yale"],
  "Jensen Huang": ["Stanford"],
  "Jodi Picoult": ["Harvard"],
  "Joe Biden": ["Syracuse"],
  "John Grisham": ["Ole Miss"],
  "John Kerry": ["Boston College"],
  "Kamala Harris": ["UC Hastings"],
  "Keegan-Michael Key": ["Penn State"],
  "Ken Jeong": ["North Carolina"],
  "Ken Lay": ["Houston"],
  "Kris Kristofferson": ["Oxford"],
  "Larry Page": ["Stanford"],
  "Marco Rubio": ["Miami (FL)"],
  "Meg Whitman": ["Harvard"],
  "Meryl Streep": ["Yale"],
  "Michael Bay": ["Art Center College of Design"],
  "Michelle Obama": ["Harvard"],
  "Mitt Romney": ["Harvard"],
  "Paul Newman": ["Yale"],
  "Pete Buttigieg": ["Oxford"],
  "Phil Knight": ["Stanford"],
  "RFK Jr.": ["London School of Economics", "Pace", "Virginia"],
  "Rachel Maddow": ["Oxford"],
  "Reid Hoffman": ["Oxford"],
  "Ryan Coogler": ["USC"],
  "Sergey Brin": ["Stanford"],
  "Steve Ballmer": ["Stanford"],
  "Ted Cruz": ["Harvard"],
  "Theodore Roosevelt": ["Columbia"],
  "Tim Cook": ["Duke"],
  "Toni Morrison": ["Cornell"],
  "Ty Burrell": ["Penn State"],
  "Viola Davis": ["Juilliard"],
  "Warren Buffett": ["Columbia"],

  // --- 2026-10-29 roster batch (+90) ---
  "Jim Parsons": ["San Diego"],
  "Rainn Wilson": ["NYU"],
  "Beth Mowins": ["Syracuse"],
  "Michael Bloomberg": ["Harvard"],
  "Doris Burke": ["Providence"],
  "Jay Bilas": ["Duke"],
};

/**
 * Other UNDERGRADUATE schools this player attended. These are accepted as
 * correct answers in hard mode.
 */
export function getAltColleges(playerName) {
  return UNDERGRAD_ALTS[playerName] || [];
}

/**
 * Every school this player attended beyond the roster college, undergrad AND
 * postgrad. Used to keep real schools out of the multiple-choice distractors.
 */
export function getExcludedColleges(playerName) {
  const ug = UNDERGRAD_ALTS[playerName] || [];
  const pg = POSTGRAD_SCHOOLS[playerName] || [];
  return pg.length ? [...ug, ...pg] : ug;
}
